/**
 * «Прогон мониторинга» — таблица одного обхода и заказ постов по строкам
 * (docs/release-2026-09-19.md §3.6, §4). Открывается кнопкой в шапке.
 *
 * ЧТО ЭТО. Всё, что модель мониторинга оценила за один обход: заголовок, одна строка сути,
 * источник, дата и оценка, а справа — что со статьёй СЕЙЧАС: пост готов, пишется, тему по ней
 * уже завёл сам мониторинг или его можно заказать. Статус считает бэкенд по всем заявкам,
 * темам и черновикам, а не по этому прогону (§3.3), поэтому одна и та же статья в двух
 * обходах показывает один и тот же пост.
 *
 * ЗАКАЗ — ЭТО ОЧЕРЕДЬ, А НЕ ОБЕЩАНИЕ ПОСТА. «Написать» ставит заявку «по ссылке статьи»
 * (`POST /api/v1/monitoring/picks`): мониторинг перечитает статью, писатель напишет пост.
 * Партия — не «всё или ничего»: строку, которую успела заказать другая вкладка, бэкенд
 * пропускает с причиной, и причина видна у строки, а остальные встают в очередь. Первую
 * поставленную заявку диалог отдаёт наверх (`onPicked`) — полоса хода в шапке показывает
 * её сразу, как и заявку из «Запустить генерацию».
 *
 * НИКАКОГО МЕЛКОГО ТЕКСТА (§1). Всё, что объясняет, живёт в подсказке у величины (`Hint`)
 * или в ⓘ у действия; в теле экрана — только данные и действия.
 *
 * ПОДСКАЗКА — ПУЗЫРЬ У ОКНА, А НЕ У ЯЧЕЙКИ. Таблица лежит в `.tblwrap`, который прокручивается
 * по горизонтали и режет по вертикали (скруглённые углы), а сам диалог — в карточке с
 * прокруткой. Пузырь, привязанный к ячейке, обрезался бы у последних строк ровно там, где
 * его читают. Поэтому пузырь рисуется порталом в `document.body` с `position: fixed` у
 * якоря (геометрия — `.tip` из `WriterModelSwitch.module.css`), а текст подсказки лежит
 * рядом с якорем скрытым узлом и связан `aria-describedby`: скрытый узел, на который
 * ссылаются прямо, в описание попадает (accname §2A), и чтению с экрана пузырь не нужен.
 *
 * ОБНОВЛЕНИЕ (§3.6): при открытии и после каждого действия; пока скан идёт — опрос списка
 * обходов раз в 5 с (`useMonitoringRuns`), и по его завершении таблица сама переходит на
 * новый прогон. ИСКЛЮЧЕНИЕ ОДНО: у человека отмечены строки — выбор не сбрасывается из-под
 * руки, новый прогон просто появляется в списке прогонов.
 *
 * ВЫБРАННЫЙ ПРОГОН ЗАКРЕПЛЯЕТСЯ, а не следует за верхом списка: иначе скан, чей прогон
 * появляется в списке ещё идущим, увёл бы таблицу на пустой прогон до того, как модель
 * что-либо оценила.
 */

import {
  CreateMonitoringScanResponseSchema,
  MONITORING_PICK_SKIP_REASONS,
  MONITORING_PICKS_MAX,
  MONITORING_RUNS_DEFAULT_LIMIT,
} from '@smm/contracts/schemas';
import {
  type FocusEvent,
  type JSX,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'wouter';
import { ApiRequestError, post } from '../api/client.ts';
import {
  type BrandsResponse,
  type GenerationRequest,
  type MonitoringItem,
  type MonitoringItemStatus,
  MonitoringItemsResponseSchema,
  type MonitoringRunSummary,
  type MonitoringScan,
} from '../api/types.ts';
import { useResource } from '../api/useResource.ts';
import { plural, wallShort } from './format.ts';
import { IconExternal, IconInfo, IconWarn } from './icons.tsx';
import { Modal } from './Modal.tsx';
import styles from './MonitoringRunDialog.module.css';
import {
  DEFAULT_SORT,
  isPickable,
  observeScan,
  RUN_KIND_WORD,
  ratingText,
  runOptionLabel,
  runStateWord,
  type SortKey,
  type SortOrder,
  scanActive,
  sortItems,
  useMonitoringRuns,
} from './monitoringRuns.ts';
import { statusLabel } from './status.ts';

type SkipReason = (typeof MONITORING_PICK_SKIP_REASONS)[number];
type TopicStatus = Extract<MonitoringItemStatus, { readonly kind: 'topic' }>;

/** Почему строку не поставили в очередь — словами, в чип у строки. */
const SKIP_WORD: Readonly<Record<SkipReason, string>> = {
  already_writing: 'уже пишется',
  draft_exists: 'пост уже есть',
  not_found: 'статьи нет',
  queue_full: 'очередь полна',
};

/**
 * Сколько висит чип пропуска. Он нужен ровно до того, как человек его прочтёт: после
 * перечитывания таблицы строка и так показывает правду («Пишется…», «Пост готов»), и
 * вечный чип стал бы шумом поверх неё.
 */
const SKIP_SHOW_MS = 8000;

/** Кто держит заказ: идентификатор статьи у кнопки строки, это слово — у полосы выбора. */
const BATCH = 'batch';

const isSkipReason = (v: unknown): v is SkipReason =>
  typeof v === 'string' && (MONITORING_PICK_SKIP_REASONS as readonly string[]).includes(v);

interface PickOutcome {
  readonly materialId: string;
  readonly request: GenerationRequest | null;
  readonly skipped: SkipReason | null;
}

/**
 * Ответ заказа — разбором ПОЛЕЙ, которые читаем, а не строгой схемой. Заказ к этому моменту
 * уже записан, и строгий разбор, споткнувшийся о лишнее поле заявки, сказал бы человеку «не
 * вышло» про то, что вышло, — после чего он нажал бы ещё раз. Заявка передаётся полосе хода
 * как есть: та проверяет форму своих полей сама (`RunStatusBar.stepsOf`).
 */
function outcomesOf(body: unknown): PickOutcome[] | null {
  if (typeof body !== 'object' || body === null) return null;
  const results = (body as { readonly results?: unknown }).results;
  if (!Array.isArray(results)) return null;
  const out: PickOutcome[] = [];
  for (const raw of results as readonly unknown[]) {
    if (typeof raw !== 'object' || raw === null) continue;
    const r = raw as {
      readonly material_id?: unknown;
      readonly request?: unknown;
      readonly skipped?: unknown;
    };
    if (typeof r.material_id !== 'string') continue;
    const skipped = isSkipReason(r.skipped) ? r.skipped : null;
    const request =
      skipped === null &&
      typeof r.request === 'object' &&
      r.request !== null &&
      typeof (r.request as { readonly request_id?: unknown }).request_id === 'string'
        ? (r.request as GenerationRequest)
        : null;
    out.push({ materialId: r.material_id, request, skipped });
  }
  return out;
}

/**
 * Какую заявку показать в шапке: ту, которую писатель возьмёт ПЕРВОЙ, — очередь разбирается
 * с головы (`created_at`, затем id). Самая свежая стояла бы «в очереди» всё время, пока
 * пишутся предыдущие.
 */
function firstInLine(requests: readonly GenerationRequest[]): GenerationRequest | null {
  let best: GenerationRequest | null = null;
  for (const r of requests) {
    if (
      best === null ||
      r.created_at < best.created_at ||
      (r.created_at === best.created_at && r.request_id < best.request_id)
    ) {
      best = r;
    }
  }
  return best;
}

const messageOf = (e: unknown): string =>
  e instanceof ApiRequestError || e instanceof Error ? e.message : String(e);

/**
 * Отказ скана словами сервиса, а не молча погасшая точка: «идёт» сменилось бы на «можно
 * запустить», и человек решил бы, что обход прошёл. Точка в конце у сервиса бывает — вторая
 * читалась бы опиской.
 */
function scanFailureText(scan: MonitoringScan): string {
  const why = (scan.outcome_detail ?? 'причина не записана').trim().replace(/\.$/, '');
  return `Мониторинг не прошёл: ${why}.`;
}

function timezoneOf(brands: BrandsResponse | undefined, brand: string): string | null {
  if (!Array.isArray(brands)) return null;
  return brands.find((b) => b.id === brand)?.timezone ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Подсказка

/** Геометрия пузыря — `.tip` из `WriterModelSwitch.module.css`: 300px, отступ 8px от якоря. */
const TIP_WIDTH = 300;
const TIP_GAP = 8;
const TIP_EDGE = 8;

interface TipPlace {
  readonly left: number;
  readonly width: number;
  readonly top: number | null;
  readonly bottom: number | null;
}

/**
 * Куда встать пузырю. Под якорем, если якорь в верхней половине окна, иначе над ним; по
 * горизонтали — по центру якоря, но не за краем окна. Высоту пузыря не меряем: половина окна
 * заведомо больше пяти строк подсказки, а второй проход раскладки ради точной высоты — это
 * мигание пузыря на каждом наведении.
 */
function placeTip(r: DOMRect): TipPlace {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.max(0, Math.min(TIP_WIDTH, vw - 2 * TIP_EDGE));
  const left = Math.min(
    Math.max(r.left + r.width / 2 - width / 2, TIP_EDGE),
    Math.max(TIP_EDGE, vw - width - TIP_EDGE),
  );
  const below = r.top + r.height / 2 < vh / 2;
  return {
    left,
    width,
    top: below ? r.bottom + TIP_GAP : null,
    bottom: below ? null : vh - r.top + TIP_GAP,
  };
}

/** То, что якорь подсказки обязан на себя надеть: описание и четыре события. */
interface HintAnchor {
  readonly 'aria-describedby': string;
  readonly onMouseEnter: (e: MouseEvent<HTMLElement>) => void;
  readonly onMouseLeave: () => void;
  readonly onFocus: (e: FocusEvent<HTMLElement>) => void;
  readonly onBlur: () => void;
}

/**
 * Подсказка у величины. Якорь — ВСЕГДА фокусируемый элемент (кнопка или ссылка): подсказку
 * обязана открывать и клавиатура, а не только мышь. События вешаются на сам якорь, а не на
 * обёртку: обёртка с обработчиками была бы «интерактивным ничем» для чтения с экрана.
 */
function Hint({
  content,
  children,
}: {
  readonly content: ReactNode;
  readonly children: (anchor: HintAnchor, show: (e: MouseEvent<HTMLElement>) => void) => ReactNode;
}): JSX.Element {
  const id = useId();
  const [place, setPlace] = useState<TipPlace | null>(null);

  useEffect(() => {
    if (place === null) return undefined;
    // Пузырь приклеен к окну, а не к строке: прокрутка карточки или таблицы увела бы строку
    // из-под него. Прокрутка не всплывает — слушаем в фазе перехвата.
    const off = (): void => {
      setPlace(null);
    };
    window.addEventListener('scroll', off, true);
    window.addEventListener('resize', off);
    return () => {
      window.removeEventListener('scroll', off, true);
      window.removeEventListener('resize', off);
    };
  }, [place]);

  const show = (e: { readonly currentTarget: Element }): void => {
    setPlace(placeTip(e.currentTarget.getBoundingClientRect()));
  };
  const hide = (): void => {
    setPlace(null);
  };

  const anchor: HintAnchor = {
    'aria-describedby': id,
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: hide,
  };

  return (
    <>
      {children(anchor, show)}
      <span id={id} hidden>
        {content}
      </span>
      {place === null
        ? null
        : createPortal(
            <span
              className={styles['tip']}
              aria-hidden="true"
              data-testid="hint-bubble"
              style={{
                left: place.left,
                width: place.width,
                ...(place.top === null ? {} : { top: place.top }),
                ...(place.bottom === null ? {} : { bottom: place.bottom }),
              }}
            >
              {content}
            </span>,
            document.body,
          )}
    </>
  );
}

/** ⓘ у действия: кнопка без собственного действия, кроме «показать подсказку». */
function InfoButton({
  label,
  content,
}: {
  readonly label: string;
  readonly content: ReactNode;
}): JSX.Element {
  return (
    <Hint content={content}>
      {(anchor, show) => (
        <button
          type="button"
          className={styles['info']}
          aria-label={label}
          {...anchor}
          onClick={show}
        >
          <IconInfo />
        </button>
      )}
    </Hint>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Ячейки

function DateValue({
  item,
  timezone,
}: {
  readonly item: MonitoringItem;
  readonly timezone: string | null;
}): JSX.Element {
  const text = wallShort(item.published_at, timezone);
  if (item.published_at_known) return <span className={styles['date']}>{text}</span>;
  // Дата не из источника: `published_at` у материала тотален (момент выборки, если разобрать
  // не удалось), и без пометки подставленная дата неотличима от настоящей.
  return (
    <Hint content={<span>Дата не из источника — момент, когда мониторинг забрал статью.</span>}>
      {(anchor, show) => (
        <button
          type="button"
          className={`${styles['plain']} ${styles['date']}`}
          {...anchor}
          onClick={show}
        >
          ≈ {text}
        </button>
      )}
    </Hint>
  );
}

function RatingValue({ item }: { readonly item: MonitoringItem }): JSX.Element {
  const text = ratingText(item.rating);
  const pct = Math.round(Math.min(1, Math.max(0, item.rating)) * 100);
  return (
    <Hint
      content={
        <>
          <b>
            Оценка {text} · {item.stage === 'deep' ? 'по полному тексту' : 'по заголовку и лиду'}
          </b>
          {item.relevant ? null : <span>Модель сочла материал нерелевантным.</span>}
          {item.why === null ? null : <span>{item.why}</span>}
          {item.angle === null ? null : <span>Угол: {item.angle}</span>}
          {item.picked ? (
            <span className={styles['tipNote']}>Мониторинг сам взял статью в тему.</span>
          ) : null}
        </>
      }
    >
      {(anchor, show) => (
        <button
          type="button"
          className={`${styles['plain']} ${styles['rating']}`}
          aria-label={`Оценка ${text}`}
          {...anchor}
          onClick={show}
        >
          <span className={styles['ratingNum']}>{text}</span>
          <span className={styles['meter']} aria-hidden="true">
            <span className={styles['meterFill']} style={{ width: `${String(pct)}%` }} />
          </span>
        </button>
      )}
    </Hint>
  );
}

interface StatusActionProps {
  readonly item: MonitoringItem;
  readonly brand: string;
  readonly timezone: string | null;
  /** Эта строка заказывается прямо сейчас. */
  readonly busy: boolean;
  /** Идёт любой заказ: вторая заявка поверх первой ждёт её ответа. */
  readonly locked: boolean;
  readonly skip: SkipReason | undefined;
  readonly onWrite: () => void;
  readonly onOpen: () => void;
}

/**
 * Подпись строки, по статье которой тему завёл сам мониторинг (`topic`): `proposed` — тема
 * ждёт писателя, `selected` — писатель её уже взял. Заказать такую статью нельзя, а кнопки
 * «Написать» без объяснения человек искал бы глазами — объяснение в подсказке.
 */
const TOPIC_WORD: Readonly<Record<TopicStatus['topic_status'], string>> = {
  proposed: 'В очереди',
  selected: 'Пишется…',
};

const TOPIC_HINT = 'Тему по этой статье завёл мониторинг — пост напишет сервис генерации';

function StatusAction({
  item,
  brand,
  timezone,
  busy,
  locked,
  skip,
  onWrite,
  onOpen,
}: StatusActionProps): JSX.Element {
  const s = item.status;
  // `switch` без `default`, а не цепочка if/else: вид статуса, добавленный в контракт, без
  // своей ветки не компилируется («action используется до присваивания» — тип `JSX.Element`,
  // а не `ReactNode`, в котором есть `undefined`), а не рисует молча «Написать» — ровно так
  // `topic` и ушёл бы в заказ второго поста о той же статье.
  let action: JSX.Element;
  switch (s.kind) {
    case 'draft':
      action = (
        <Hint
          content={
            <>
              <b>Открыть пост №{s.post_no}</b>
              <span>{statusLabel(s.draft_status)}</span>
            </>
          }
        >
          {(anchor) => (
            <Link
              href={`/b/${encodeURIComponent(brand)}/draft/${encodeURIComponent(s.draft_id)}`}
              className={`btn btn-sm ${styles['outline']}`}
              {...anchor}
              onClick={onOpen}
            >
              Пост готов
            </Link>
          )}
        </Hint>
      );
      break;
    case 'writing':
      action = (
        <button type="button" className="btn btn-sm btn-dis" disabled>
          Пишется…
        </button>
      );
      break;
    case 'topic':
      // Вид — как у `writing`, но `aria-disabled`, а не `disabled`: выключенная кнопка не
      // получает ни фокуса, ни наведения, и подсказка «почему нельзя» была бы недостижима
      // именно там, где её ищут. Нажатие только показывает подсказку — действия у строки нет.
      action = (
        <Hint content={<span>{TOPIC_HINT}</span>}>
          {(anchor, show) => (
            <button
              type="button"
              className="btn btn-sm btn-dis"
              aria-disabled="true"
              data-testid="monitoring-topic-status"
              {...anchor}
              onClick={show}
            >
              {TOPIC_WORD[s.topic_status]}
            </button>
          )}
        </Hint>
      );
      break;
    case 'none':
    case 'failed':
      action = (
        <>
          {s.kind === 'failed' ? (
            <Hint
              content={
                <>
                  <b>Прошлая попытка не удалась</b>
                  <span>{s.detail ?? 'Причина не записана.'}</span>
                  <span className={styles['tipNote']}>{wallShort(s.finished_at, timezone)}</span>
                </>
              }
            >
              {(anchor, show) => (
                <button
                  type="button"
                  className={styles['failDot']}
                  aria-label="Прошлая попытка не удалась"
                  data-testid="monitoring-failed-dot"
                  {...anchor}
                  onClick={show}
                />
              )}
            </Hint>
          ) : null}
          <button
            type="button"
            className={locked ? 'btn btn-sm btn-dis' : 'btn btn-pri btn-sm'}
            disabled={locked}
            aria-busy={busy ? true : undefined}
            aria-label={`Написать пост: ${item.title}`}
            onClick={onWrite}
          >
            Написать
          </button>
        </>
      );
      break;
  }
  return (
    <div className={styles['status']}>
      {action}
      {skip === undefined ? null : (
        <span className={`pill st-redo ${styles['skip']}`}>{SKIP_WORD[skip]}</span>
      )}
    </div>
  );
}

interface ItemRowProps {
  readonly item: MonitoringItem;
  readonly brand: string;
  readonly timezone: string | null;
  readonly selected: boolean;
  /** Выбор ещё не упёрся в потолок одного нажатия. */
  readonly roomLeft: boolean;
  readonly busy: boolean;
  readonly locked: boolean;
  readonly skip: SkipReason | undefined;
  readonly onToggle: () => void;
  readonly onWrite: () => void;
  readonly onOpen: () => void;
}

function ItemRow({
  item,
  brand,
  timezone,
  selected,
  roomLeft,
  busy,
  locked,
  skip,
  onToggle,
  onWrite,
  onOpen,
}: ItemRowProps): JSX.Element {
  const pickable = isPickable(item);
  const capped = !selected && !roomLeft;
  return (
    <tr
      className={item.relevant ? styles['row'] : `${styles['row']} ${styles['dim']}`}
      data-testid="monitoring-item"
    >
      <td className={`${styles['td']} ${styles['cCheck']}`}>
        {pickable ? (
          <input
            type="checkbox"
            className={styles['check']}
            checked={selected}
            disabled={locked || capped}
            title={capped ? `Не больше ${String(MONITORING_PICKS_MAX)} за раз` : undefined}
            aria-label={`Выбрать: ${item.title}`}
            onChange={onToggle}
          />
        ) : null}
      </td>
      <td className={`${styles['td']} ${styles['cTitle']}`}>
        <div className={styles['titleLine']}>
          <span className={styles['title']} title={item.title}>
            {item.title}
          </span>
          <a
            className={styles['ext']}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Статья в новой вкладке: ${item.title}`}
            title="Статья в новой вкладке"
          >
            <IconExternal width={14} height={14} />
          </a>
        </div>
      </td>
      <td className={`${styles['td']} ${styles['cGist']}`}>
        {item.gist === null ? (
          <span className={styles['nil']}>—</span>
        ) : (
          <span className={styles['gist']} title={item.gist}>
            {item.gist}
          </span>
        )}
      </td>
      <td className={`${styles['td']} ${styles['cSource']}`}>
        <span
          className={styles['source']}
          title={
            item.source_title === null
              ? item.source_host
              : `${item.source_title} · ${item.source_host}`
          }
        >
          {item.source_title ?? item.source_host}
        </span>
      </td>
      <td className={`${styles['td']} ${styles['cDate']}`}>
        <DateValue item={item} timezone={timezone} />
      </td>
      <td className={`${styles['td']} ${styles['cRating']}`}>
        <RatingValue item={item} />
      </td>
      <td className={`${styles['td']} ${styles['cStatus']}`}>
        <StatusAction
          item={item}
          brand={brand}
          timezone={timezone}
          busy={busy}
          locked={locked}
          skip={skip}
          onWrite={onWrite}
          onOpen={onOpen}
        />
      </td>
    </tr>
  );
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  readonly label: string;
  readonly sortKey: SortKey;
  readonly sort: SortOrder;
  readonly onSort: (key: SortKey) => void;
}): JSX.Element {
  const on = sort.key === sortKey;
  return (
    <th
      scope="col"
      className={`${styles['th']} ${styles['sortable']}`}
      aria-sort={on ? (sort.dir === 'desc' ? 'descending' : 'ascending') : undefined}
    >
      <button
        type="button"
        className={on ? `${styles['sortBtn']} ${styles['sortOn']}` : styles['sortBtn']}
        onClick={() => {
          onSort(sortKey);
        }}
      >
        {label}
        <span className={styles['arrow']} aria-hidden="true">
          {on ? (sort.dir === 'desc' ? '↓' : '↑') : '↕'}
        </span>
      </button>
    </th>
  );
}

function ScanButton({
  scanning,
  busy,
  onStart,
  label,
  primary,
}: {
  readonly scanning: boolean;
  readonly busy: boolean;
  readonly onStart: () => void;
  readonly label: string;
  readonly primary: boolean;
}): JSX.Element {
  const look = primary ? 'btn btn-pri' : `btn btn-sm ${styles['outline']}`;
  if (scanning) {
    return (
      <button type="button" className={primary ? 'btn btn-dis' : look} disabled>
        <span className={styles['dot']} aria-hidden="true" data-testid="monitoring-scan-running" />
        Мониторинг идёт…
      </button>
    );
  }
  return (
    <button
      type="button"
      className={busy && primary ? 'btn btn-dis' : look}
      disabled={busy}
      onClick={onStart}
    >
      {label}
    </button>
  );
}

function RunMeta({
  run,
  timezone,
}: {
  readonly run: MonitoringRunSummary;
  readonly timezone: string | null;
}): JSX.Element {
  const state = runStateWord(run);
  return (
    <span className={styles['meta']} data-testid="monitoring-run-meta">
      {`${wallShort(run.started_at, timezone)} · ${RUN_KIND_WORD[run.kind]} · оценено ${String(run.items_total)}, релевантных ${String(run.items_relevant)}`}
      {state === null ? null : ` · ${state}`}
      {run.error_text === null ? null : (
        <Hint content={<span>{run.error_text}</span>}>
          {(anchor, show) => (
            <button
              type="button"
              className={`${styles['plain']} ${styles['warn']}`}
              aria-label="Ошибка прогона"
              {...anchor}
              onClick={show}
            >
              <IconWarn />
            </button>
          )}
        </Hint>
      )}
    </span>
  );
}

const GUIDE = (
  <>
    <b>Что это</b>
    <span>
      Всё, что мониторинг оценил за один обход. Статус справа — по всем заявкам и черновикам, а не
      только по этому прогону.
    </span>
    <b>Как читать оценку</b>
    <span>
      От 0 до 10: насколько материал подходит каналу. Если модель дочитала статью — по полному
      тексту, иначе по заголовку и лиду. Бледные строки модель сочла нерелевантными.
    </span>
    <span className={styles['tipNote']}>
      «Написать» ставит пост по статье в очередь писателя. «Только мониторинг» — обход и оценка без
      писателя: ни одной темы.
    </span>
  </>
);

// ─────────────────────────────────────────────────────────────────────────────
// Диалог

export interface MonitoringRunDialogProps {
  readonly brand: string;
  readonly onClose: () => void;
  /** Первая поставленная заявка — полосе хода в шапке (см. шапку файла). */
  readonly onPicked?: ((request: GenerationRequest) => void) | undefined;
  /** Открыть на этом прогоне, а не на свежем. */
  readonly initialRunId?: string | null;
}

export function MonitoringRunDialog({
  brand,
  onClose,
  onPicked,
  initialRunId = null,
}: MonitoringRunDialogProps): JSX.Element {
  const runs = useMonitoringRuns(brand, {
    limit: MONITORING_RUNS_DEFAULT_LIMIT,
    enabled: true,
    idleMs: null,
  });
  const brands = useResource<BrandsResponse>('/api/v1/brands');
  const timezone = timezoneOf(brands.data, brand);

  const [chosen, setChosen] = useState<string | null>(initialRunId);
  const firstRunId = runs.data?.runs[0]?.run_id ?? null;
  const runId = chosen ?? firstRunId;
  useEffect(() => {
    // Закрепить свежий прогон, как только он известен: дальше таблицу двигают только человек
    // и завершившийся скан (см. шапку файла).
    if (chosen === null && firstRunId !== null) setChosen(firstRunId);
  }, [chosen, firstRunId]);

  const items = useResource<unknown>(
    runId === null
      ? null
      : `/api/v1/monitoring/runs/${encodeURIComponent(runId)}/items?brand=${encodeURIComponent(brand)}`,
  );
  const parsed = useMemo(
    () => (items.data === undefined ? null : MonitoringItemsResponseSchema.safeParse(items.data)),
    [items.data],
  );
  const table = parsed?.success === true && parsed.data.run.run_id === runId ? parsed.data : null;
  const refetchItems = items.refetch;

  const [sort, setSort] = useState<SortOrder>(DEFAULT_SORT);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [picking, setPicking] = useState<string | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [skips, setSkips] = useState<ReadonlyMap<string, SkipReason>>(() => new Map());
  const [announce, setAnnounce] = useState('');
  const [scanBusy, setScanBusy] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanFailure, setScanFailure] = useState<string | null>(null);
  /** Заявка скана, которую при нас видели идущей: только её завершение двигает таблицу. */
  const watched = useRef<string | null>(null);

  const rows = useMemo(() => (table === null ? [] : sortItems(table.items, sort)), [table, sort]);
  const pickableIds = useMemo(
    () => new Set(rows.filter(isPickable).map((r) => r.material_id)),
    [rows],
  );
  // Выбор, который ещё можно заказать: строка, которую тем временем заказали в другой
  // вкладке, из выбора выпадает сама, как только перечитанная таблица это покажет.
  const chosenIds = [...selected].filter((id) => pickableIds.has(id));
  const count = chosenIds.length;
  const locked = picking !== null;

  const scan = runs.scan;
  const runList = runs.data?.runs;
  const nothingSelected = count === 0;

  // Сорвавшийся скан, исход которого человек ещё не видел: заказанный и сорвавшийся при
  // закрытом диалоге (ревью партии, U-B4) — о нём шапка держит красную точку. Открытый диалог
  // говорит об отказе словами и отмечает показанный исход просмотренным: открыть диалог и
  // значит узнать (`observeScan`, `monitoringRuns.ts`).
  const polled = runs.data;
  useEffect(() => {
    if (polled === undefined) return;
    if (observeScan(brand, scan, true) && scan !== null) setScanFailure(scanFailureText(scan));
  }, [brand, polled, scan]);

  useEffect(() => {
    if (scan === null) return;
    if (scanActive(scan)) {
      watched.current = scan.request_id;
      return;
    }
    // Скан кончился, но мы не видели его идущим — таблица уже открыта на свежем прогоне.
    if (watched.current !== scan.request_id) return;
    watched.current = null;
    if (scan.state === 'failed') {
      // Тот же отказ скажет и `observeScan` выше — эта ветка держит прежнее поведение там, где
      // хранилища нет: скан, который диалог видел идущим, сообщается и без него.
      setScanFailure(scanFailureText(scan));
      return;
    }
    const list = runList ?? [];
    const target =
      scan.run_id !== null && list.some((r) => r.run_id === scan.run_id)
        ? scan.run_id
        : (list[0]?.run_id ?? null);
    if (target === null || !nothingSelected) return;
    if (target === runId) {
      refetchItems();
    } else {
      setChosen(target);
      setSkips(new Map());
    }
  }, [scan, runList, nothingSelected, runId, refetchItems]);

  useEffect(() => {
    if (skips.size === 0) return undefined;
    const t = setTimeout(() => {
      setSkips(new Map());
    }, SKIP_SHOW_MS);
    return () => {
      clearTimeout(t);
    };
  }, [skips]);

  const chooseRun = (id: string): void => {
    setChosen(id);
    setSelected(new Set());
    setSkips(new Map());
    setPickError(null);
  };

  const toggleSort = (key: SortKey): void => {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' },
    );
  };

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((x) => pickableIds.has(x)));
      if (next.has(id)) next.delete(id);
      else if (next.size < MONITORING_PICKS_MAX) next.add(id);
      return next;
    });
  };

  const pick = (ids: readonly string[], who: string): void => {
    if (picking !== null || ids.length === 0) return;
    setPicking(who);
    setPickError(null);
    setSkips(new Map());
    post<unknown>('/api/v1/monitoring/picks', {
      brand,
      material_ids: ids,
      style_version_id: null,
    })
      .then((body) => {
        setSelected((prev) => {
          const next = new Set(prev);
          for (const id of ids) next.delete(id);
          return next;
        });
        const outcomes = outcomesOf(body);
        if (outcomes === null) {
          setPickError('Заказ отправлен, но ответ сервера не разобран — статусы строк перечитаны.');
          return;
        }
        const skipped = new Map<string, SkipReason>();
        const created: GenerationRequest[] = [];
        for (const o of outcomes) {
          if (o.skipped !== null) skipped.set(o.materialId, o.skipped);
          else if (o.request !== null) created.push(o.request);
        }
        setSkips(skipped);
        const first = firstInLine(created);
        if (first !== null) onPicked?.(first);
        setAnnounce(
          created.length === 0
            ? `Ни один пост не заказан, пропущено: ${String(skipped.size)}.`
            : `Поставлено в очередь: ${String(created.length)}${skipped.size === 0 ? '' : `, пропущено: ${String(skipped.size)}`}.`,
        );
      })
      .catch((e: unknown) => {
        setPickError(`Не удалось заказать: ${messageOf(e)}`);
      })
      .finally(() => {
        setPicking(null);
        // После любого исхода — перечитать: даже оборванная связь могла прийти уже после
        // записи, и правду о строках знает только сервер.
        refetchItems();
      });
  };

  const startScan = (): void => {
    if (scanBusy || runs.scanning) return;
    setScanBusy(true);
    setScanError(null);
    setScanFailure(null);
    post<unknown>('/api/v1/monitoring/scan', { brand })
      .then((body) => {
        const answer = CreateMonitoringScanResponseSchema.safeParse(body);
        if (answer.success) {
          watched.current = answer.data.scan.request_id;
          runs.noteScan(answer.data.scan);
        } else {
          runs.refresh();
        }
      })
      .catch((e: unknown) => {
        setScanError(`Мониторинг не запустился: ${messageOf(e)}`);
      })
      .finally(() => {
        setScanBusy(false);
      });
  };

  const list = runs.data?.runs ?? [];
  const run: MonitoringRunSummary | null =
    list.find((r) => r.run_id === runId) ?? (table === null ? null : table.run);
  const options: readonly MonitoringRunSummary[] =
    run !== null && !list.some((r) => r.run_id === run.run_id) ? [...list, run] : list;

  const alerts = [scanError, scanFailure, pickError].filter((a): a is string => a !== null);

  let body: ReactNode;
  if (runs.data === undefined) {
    body =
      runs.error === undefined ? (
        <div className={styles['state']}>Загружаем прогоны…</div>
      ) : (
        <div className="val val-err" role="alert">
          <IconWarn />
          <span className={styles['grow']}>Прогоны не загрузились: {runs.error.message}</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={runs.refresh}>
            Повторить
          </button>
        </div>
      );
  } else if (runId === null) {
    body = (
      <div className={styles['empty']}>
        <span className={styles['emptyTitle']}>Мониторинг ещё не ходил</span>
        <ScanButton
          scanning={runs.scanning}
          busy={scanBusy}
          onStart={startScan}
          label="Запустить мониторинг"
          primary
        />
      </div>
    );
  } else {
    let content: ReactNode;
    if (items.error !== undefined) {
      content = (
        <div className="val val-err" role="alert">
          <IconWarn />
          <span className={styles['grow']}>
            Строки прогона не загрузились: {items.error.message}
          </span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={refetchItems}>
            Повторить
          </button>
        </div>
      );
    } else if (parsed?.success === false) {
      content = (
        <div className="val val-err" role="alert">
          <IconWarn />
          <span>Ответ сервера о строках прогона не разобран.</span>
        </div>
      );
    } else if (table === null) {
      content = <div className={styles['state']}>Загружаем строки…</div>;
    } else if (table.items.length === 0) {
      content = (
        <div className={styles['empty']}>
          <span className={styles['emptyTitle']}>
            {table.run.state === 'running'
              ? 'Прогон ещё идёт'
              : 'В этом прогоне модель ничего не оценивала'}
          </span>
        </div>
      );
    } else {
      content = (
        <div className="tblwrap">
          <table className={`tbl ${styles['table']}`}>
            <caption className={styles['srOnly']}>Материалы прогона</caption>
            <colgroup>
              <col className={styles['colCheck']} />
              <col />
              <col />
              <col className={styles['colSource']} />
              <col className={styles['colDate']} />
              <col className={styles['colRating']} />
              <col className={styles['colStatus']} />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className={`${styles['th']} ${styles['cCheck']}`}>
                  <span className={styles['srOnly']}>Выбор</span>
                </th>
                <th scope="col" className={styles['th']}>
                  Новость
                </th>
                <th scope="col" className={styles['th']}>
                  Кратко
                </th>
                <th scope="col" className={styles['th']}>
                  Источник
                </th>
                <SortHeader label="Дата" sortKey="date" sort={sort} onSort={toggleSort} />
                <SortHeader label="Оценка" sortKey="rating" sort={sort} onSort={toggleSort} />
                <th scope="col" className={styles['th']}>
                  <span className={styles['srOnly']}>Пост</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <ItemRow
                  key={item.material_id}
                  item={item}
                  brand={brand}
                  timezone={timezone}
                  selected={chosenIds.includes(item.material_id)}
                  roomLeft={count < MONITORING_PICKS_MAX}
                  busy={picking === item.material_id}
                  locked={locked}
                  skip={skips.get(item.material_id)}
                  onToggle={() => {
                    toggle(item.material_id);
                  }}
                  onWrite={() => {
                    pick([item.material_id], item.material_id);
                  }}
                  onOpen={onClose}
                />
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    body = (
      <>
        <div className={styles['toolbar']}>
          <div className={styles['lead']}>
            <InfoButton label="Что это и как читать оценку" content={GUIDE} />
            {run === null ? null : <RunMeta run={run} timezone={timezone} />}
          </div>
          <div className={styles['controls']}>
            <select
              className={styles['select']}
              aria-label="Прогон"
              value={runId}
              onChange={(e) => {
                chooseRun(e.target.value);
              }}
            >
              {run === null ? <option value={runId}>…</option> : null}
              {options.map((r) => (
                <option key={r.run_id} value={r.run_id}>
                  {runOptionLabel(r, timezone)}
                </option>
              ))}
            </select>
            <ScanButton
              scanning={runs.scanning}
              busy={scanBusy}
              onStart={startScan}
              label="Только мониторинг"
              primary={false}
            />
          </div>
        </div>
        {content}
      </>
    );
  }

  return (
    <Modal title="Прогон мониторинга" width={1180} onClose={onClose}>
      {body}

      {alerts.map((text) => (
        <div key={text} className="val val-err" role="alert">
          <IconWarn />
          <span>{text}</span>
        </div>
      ))}

      {count === 0 ? null : (
        <section className={styles['selection']} aria-label="Выбранные статьи">
          <span className={styles['count']}>Выбрано: {count}</span>
          <span className={styles['spacer']} />
          <InfoButton
            label="Как пишется серия"
            content={
              <span>
                Пишутся друг за другом, общая часть промпта берётся из кэша — серия дешевле. Не
                больше {MONITORING_PICKS_MAX} за раз.
              </span>
            }
          />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={locked}
            onClick={() => {
              setSelected(new Set());
            }}
          >
            Снять выбор
          </button>
          <button
            type="button"
            className={locked ? 'btn btn-dis' : 'btn btn-pri'}
            disabled={locked}
            aria-busy={picking === BATCH ? true : undefined}
            onClick={() => {
              pick(chosenIds, BATCH);
            }}
          >
            Написать {count} {plural(count, 'пост', 'поста', 'постов')}
          </button>
        </section>
      )}

      <div className={styles['srOnly']} role="status" aria-live="polite">
        {announce}
      </div>
    </Modal>
  );
}

/**
 * Вход в таблицу прогона со строки раздела «Прогоны мониторинга» вкладки «Мониторинг»:
 * runbook «пропущенный прогон» ведёт оператора туда, и следующий его вопрос — «а что этот
 * обход прочитал?». Кнопка-ссылка владеет диалогом сама, поэтому вкладке достаточно обернуть
 * ею одну ячейку — ни состояния, ни стилей диалога экран мониторинга не несёт.
 *
 * Заказ отсюда до полосы хода в шапке едет фоновым опросом (≤15 с), а не мгновенно: передать
 * заявку в шапку может только сама шапка, а вкладка — её содержимое, а не владелец.
 */
export function MonitoringRunOpener({
  brand,
  runId,
  label,
}: {
  readonly brand: string;
  readonly runId: string;
  /** Видимая подпись — момент прогона в том виде, в каком его печатает таблица вкладки. */
  readonly label: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  // Одна функция на всю жизнь входа: `Modal` держит фокус эффектом, зависящим от `onClose`,
  // а вкладка перерисовывается своими загрузками — новая функция уводила бы фокус на карточку.
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  return (
    <>
      <button
        type="button"
        className={styles['opener']}
        aria-label={`Таблица прогона ${label}`}
        title="Открыть таблицу прогона"
        onClick={() => {
          setOpen(true);
        }}
      >
        {label}
      </button>
      {open ? <MonitoringRunDialog brand={brand} initialRunId={runId} onClose={close} /> : null}
    </>
  );
}
